export const SALARY_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'CHF',
  'JPY',
  'INR',
  'SGD',
  'AED',
  'NZD',
  'SEK',
  'NOK',
  'DKK',
];

export const SALARY_STRUCTURES = [
  { id: 'annual', label: 'Annual' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'daily', label: 'Daily' },
  { id: 'hourly', label: 'Hourly' },
  { id: 'project', label: 'Per project' },
];

export const SALARY_REGIONS = [
  { id: 'usCanada', label: 'US & Canada' },
  { id: 'europe', label: 'Europe' },
  { id: 'other', label: 'Other' },
];

const STRUCTURE_SHORT = {
  annual: 'year',
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  hourly: 'hour',
  project: 'project',
};

const emptyRegion = () => ({ from: '', to: '' });

export const emptyRegionalSalaries = () => ({
  usCanada: emptyRegion(),
  europe: emptyRegion(),
  other: emptyRegion(),
});

/** Full-time → annual; part-time / contract → monthly */
export const salaryPeriodFromJobType = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('part') || t.includes('contract')) return 'monthly';
  return 'annual';
};

const parseAmount = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

const fmtAmount = (n) =>
  Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '';

/**
 * Second-image style: "USD 235,000 – 345,000 annual"
 */
export const formatPeriodSalaryRange = ({
  from,
  to,
  currency = 'USD',
  period = 'annual',
} = {}) => {
  const cur = String(currency || 'USD').trim().toUpperCase() || 'USD';
  const per = period === 'monthly' ? 'monthly' : 'annual';
  const fromN = parseAmount(from);
  const toN = parseAmount(to);
  if (fromN !== null && toN !== null) {
    return `${cur} ${fmtAmount(fromN)} – ${fmtAmount(toN)} ${per}`;
  }
  if (fromN !== null) return `${cur} ${fmtAmount(fromN)}+ ${per}`;
  if (toN !== null) return `Up to ${cur} ${fmtAmount(toN)} ${per}`;
  return '';
};

export const formatSalaryDisplay = ({
  salaryFrom,
  salaryTo,
  salaryCurrency,
  salaryStructure,
  salary,
} = {}) => {
  const currency = String(salaryCurrency || 'USD').trim().toUpperCase() || 'USD';
  const structure = String(salaryStructure || 'annual').trim().toLowerCase() || 'annual';
  const suffix = STRUCTURE_SHORT[structure] ? ` / ${STRUCTURE_SHORT[structure]}` : '';
  const from = parseAmount(salaryFrom);
  const to = parseAmount(salaryTo);

  if (from !== null && to !== null) {
    return `${currency} ${fmtAmount(from)} – ${fmtAmount(to)}${suffix}`;
  }
  if (from !== null) return `${currency} ${fmtAmount(from)}+${suffix}`;
  if (to !== null) return `Up to ${currency} ${fmtAmount(to)}${suffix}`;
  return String(salary || '').trim();
};

/**
 * Rows for job detail compensation block.
 * Falls back to legacy salaryFrom/To as US & Canada when regional is empty.
 */
export const getRegionalSalaryDisplayRows = (job = {}) => {
  const period = salaryPeriodFromJobType(job.type);
  const currency = job.salaryCurrency || 'USD';
  const regional = job.regionalSalaries || {};

  const rows = SALARY_REGIONS.map(({ id, label }) => {
    const region = regional[id] || {};
    const range = formatPeriodSalaryRange({
      from: region.from,
      to: region.to,
      currency,
      period,
    });
    return { id, label, value: range };
  }).filter((row) => row.value);

  if (rows.length > 0) return rows;

  const fallback = formatPeriodSalaryRange({
    from: job.salaryFrom,
    to: job.salaryTo,
    currency,
    period,
  });
  if (!fallback) return [];
  return [{ id: 'usCanada', label: 'US & Canada', value: fallback }];
};

export const parseRegionalSalariesForm = (regional = {}) => {
  const result = {};
  SALARY_REGIONS.forEach(({ id }) => {
    const region = regional[id] || {};
    result[id] = {
      from: parseAmount(region.from),
      to: parseAmount(region.to),
    };
  });
  return result;
};

export const regionalSalariesToForm = (job = {}) => {
  const empty = emptyRegionalSalaries();
  const regional = job.regionalSalaries || {};
  SALARY_REGIONS.forEach(({ id }) => {
    const region = regional[id] || {};
    const from = region.from;
    const to = region.to;
    empty[id] = {
      from: from === null || from === undefined || from === '' ? '' : String(from),
      to: to === null || to === undefined || to === '' ? '' : String(to),
    };
  });

  const anyRegional = SALARY_REGIONS.some(({ id }) => {
    const r = empty[id];
    return r.from !== '' || r.to !== '';
  });
  if (!anyRegional && (job.salaryFrom != null || job.salaryTo != null)) {
    empty.usCanada = {
      from: job.salaryFrom != null ? String(job.salaryFrom) : '',
      to: job.salaryTo != null ? String(job.salaryTo) : '',
    };
  }
  return empty;
};
